/**
 * Lesson 4: Placing Your First Order
 *
 * Introduces market orders, instant execution, and slippage.
 * Users practice placing their first trade.
 */

import type { Lesson, SimulationScenario } from '../manager'
import type { Market, OrderBook, Order } from '../../engine/types'

// Helper to create an order
function createOrder(
  id: string,
  side: 'buy' | 'sell',
  price: number,
  quantity: number,
  timestamp: number
): Order {
  return {
    id,
    side,
    type: 'limit',
    price,
    quantity,
    timestamp,
    status: 'open',
    filledQuantity: 0,
  }
}

// Order book with good liquidity for market order practice
const marketOrderBook: OrderBook = {
  bids: [
    createOrder('bid-1', 'buy', 0.52, 100, Date.now() - 5000),
    createOrder('bid-2', 'buy', 0.51, 150, Date.now() - 4000),
    createOrder('bid-3', 'buy', 0.50, 200, Date.now() - 3000),
    createOrder('bid-4', 'buy', 0.48, 100, Date.now() - 2000),
  ],
  asks: [
    createOrder('ask-1', 'sell', 0.55, 80, Date.now() - 5000),
    createOrder('ask-2', 'sell', 0.56, 120, Date.now() - 4000),
    createOrder('ask-3', 'sell', 0.58, 150, Date.now() - 3000),
    createOrder('ask-4', 'sell', 0.60, 100, Date.now() - 2000),
  ],
  trades: [],
  lastPrice: 0.54,
}

const marketOrderMarket: Market = {
  id: 'lesson-4-market',
  question: 'Will the new product launch on time?',
  description: 'Practice placing market orders',
  outcomes: ['Yes', 'No'] as const,
  orderBook: marketOrderBook,
  resolved: false,
  resolution: null,
}

export const marketOrderScenario: SimulationScenario = {
  id: 'market-order-practice',
  name: 'Market Order Practice',
  description: 'Learn to place market orders with instant execution',
  market: marketOrderMarket,
  initialOrderBook: marketOrderBook,
  userStartingBalance: 100,
}

export const lesson4: Lesson = {
  id: 'lesson-4',
  title: 'Placing Your First Order',
  description: 'Learn how to place market orders and execute trades instantly',
  order: 4,
  prerequisites: ['lesson-3'],
  completionCriteria: { type: 'interactive-complete' },
  objectives: [
    'Understand how market orders work',
    'Place a market order to buy shares',
    'See how your position updates after trading',
  ],
  content: [
    // Page 1: What is a Market Order?
    {
      type: 'text',
      data: {
        markdown: `# Placing Your First Order

Now it's time to trade! Let's start with the simplest order type: the **market order**.

## What is a Market Order?

A **market order** says: "I want to buy (or sell) right now at the best available price."

### Characteristics
- ⚡ **Instant execution** - Your order fills immediately
- 💰 **Best available price** - Takes whatever price is offered
- ✅ **Guaranteed to fill** - As long as there's someone on the other side

### When to Use Market Orders
- You want to trade **right now**
- You care more about **getting filled** than the exact price
- The market has good **liquidity** (lots of orders)`,
      },
    },

    // Page 2: How Market Orders Execute
    {
      type: 'text',
      data: {
        markdown: `## How Market Orders Execute

When you place a market buy order, the system:

1. Looks at the order book
2. Finds the **lowest ask price** (best available)
3. Buys as many shares as available at that price
4. If you want more, moves to the **next best price**
5. Continues until your order is filled

### Example

Say you want to **buy 100 shares** with a market order:

| Ask Price | Available  | You Buy | Running Total |
|-----------|------------|---------|---------------|
| $0.55     | 80 shares  | 80      | 80            |
| $0.56     | 120 shares | 20      | **100** ✅    |

You bought:

- 80 shares at $0.55 = $44.00
- 20 shares at $0.56 = $11.20
- **Total cost: $55.20**

Your average price: $55.20 ÷ 100 = **$0.552 per share**`,
        visualizationId: 'order-book',
      },
    },

    // Page 3: Slippage
    {
      type: 'text',
      data: {
        markdown: `## Understanding Slippage

**Slippage** is when your actual execution price differs from what you expected.

In the previous example:
- You might have expected to pay $0.55 (the best ask)
- But you actually paid an average of $0.552
- The difference ($0.002) is **slippage**

### Why Slippage Happens

1. **Size**: Large orders move through multiple price levels
2. **Speed**: Prices can change between seeing and executing
3. **Liquidity**: Thin order books have more slippage

### Slippage Tips

| Order Size | Expected Slippage |
|------------|-------------------|
| Small (10-20 shares) | Little to none |
| Medium (50-100 shares) | Some slippage |
| Large (500+ shares) | Significant slippage |

> **Rule of thumb**: Start with small orders to minimize slippage while learning.`,
      },
    },

    // Page 4: Practice sandbox
    {
      type: 'sandbox',
      data: {
        scenario: marketOrderScenario,
        tools: ['market-order'],
      },
    },

    // Page 5: Interactive - Place your first order
    {
      type: 'interactive',
      data: {
        scenario: marketOrderScenario,
        instructions: `## Time to Trade!

Look at the trading panel. Let's place your first market order.

### Your Task
1. Make sure **"Market"** order type is selected
2. Enter a quantity of **10 shares**
3. Click the **Buy** button

Watch what happens:
- Your order executes **instantly**
- Your **balance decreases**
- You now have a **position** in the market

After your trade, click "Mark as Complete" to continue.`,
        hints: [
          'Find the trading panel and select "Market" order type.',
          'Enter 10 in the quantity field.',
          'Click the Buy button to execute your market order.',
          'Check "Your Positions" to see your new shares.',
        ],
        successCondition: (state) => {
          // Check if user has any position
          return state.userPositions.length > 0 && state.userPositions.some(p => p.shares > 0)
        },
      },
    },

    // Quiz
    {
      type: 'quiz',
      data: {
        question: 'What is the main advantage of a market order?',
        options: [
          { id: 'a', text: 'You get the best possible price' },
          { id: 'b', text: 'Your order executes immediately' },
          { id: 'c', text: 'There is no risk involved' },
          { id: 'd', text: 'You can set your own price' },
        ],
        correctOptionId: 'b',
        explanation:
          'The main advantage of a market order is instant execution. You trade immediately at the best available price. The tradeoff is you might experience slippage if the market moves or if you\'re trading a large quantity.',
      },
    },
  ],
}
